import React, {useState} from "react";
import {EditableTypeDto} from "../../../../dto/construction/EditableTypeDto";
import {Icon, SpinnerSize} from "@fluentui/react";
import IsaacDropDown from "../editable/IsaacDropDown";
import EditableItem from "../editable/EditableItem";
import styles from "../editable/EditableItem.module.scss";
import IsaacLoading from "../../../../components/isaacLoading/IsaacLoading";
import {useDispatch, useSelector} from "react-redux";
import type {RootState} from "../../../../store";
// import {useParams} from "react-router-dom";
import useConstructionNewHook from "../../../../hooks/useConstructionNewHook";
import {updateEditableProps} from "../../constructionNewSlice";
import IsaacNumberInput from "../editable/IsaacNumberInput";
import IsaacTextInput from "../editable/IsaacTextInput";
import IsaacCheckBox from "../editable/IsaacCheckBox";
import ConstructionTableFixedCell from "./ConstructionTableFixedCell";
import formatCell from "./constructionTabelRowCellHelpers";

interface ConstructionTableEditableCellProps {
  constructionId: string;
  constructionElementsName: string;
  constructionElementProperty: string;
  constructionElementEntityId: string | null;
  value: string | number | boolean | Record<string, unknown>;
  type?: EditableTypeDto;
  extraInfo?: { family: string };
}

const ConstructionTableEditableCell: React.FunctionComponent<ConstructionTableEditableCellProps> = ({
  constructionId,
  constructionElementsName,
  constructionElementProperty,
  constructionElementEntityId,
  value,
  type,
  extraInfo,
}) => {
  const [newValue, setNewValue] = useState<string | number | boolean | Record<string, unknown>>(value);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | undefined>();
  const [isEditing, setIsEditing] = useState(false);

  const {validateConstructionPropertyChanged} = useConstructionNewHook();

  // const {id} = useParams(); // Why not throw an Exception when id is undefined, it justs needs to be defined !
  const {defaultCalculationParams} = useSelector((state: RootState) => state.constructionNew);
  const dispatch = useDispatch();

  const validateCalculationDeformationParameters = (): string | undefined => {
    const defaultDeformation = defaultCalculationParams?.deformationParameters.find(
      df => df.id.toLocaleLowerCase() === constructionElementProperty.toLocaleLowerCase(),
    );

    if (defaultDeformation) {
      const isAboveMin = defaultDeformation.min === null || Number(newValue) >= defaultDeformation.min;
      const isBelowMax = defaultDeformation.max === null || Number(newValue) <= defaultDeformation.max;
      return isAboveMin && isBelowMax
        ? undefined
        : `Value is outside the following range ${defaultDeformation.min} <= ${newValue as string} <= ${defaultDeformation.max} `;
    }
    return "Calculation deformation parameters";
  };

  const validateProperty = async (): Promise<void> => {
    setLoading(prevState => !prevState);
    let validationResponse: string | undefined;
    // Note: validate calculation parameters first on the FE. BE will also have validation in place.
    if (
      constructionElementProperty.toLocaleLowerCase().includes("deformation") ||
      constructionElementProperty.toLocaleLowerCase().includes("overdimensioning")
    ) {
      validationResponse = validateCalculationDeformationParameters();
    } else {
      validationResponse = await validateConstructionPropertyChanged(
        constructionId,
        constructionElementsName,
        constructionElementProperty,
        constructionElementEntityId?.toString(),
        newValue as string,
      );
    }
    setLoading(prevState => !prevState);

    if (validationResponse !== undefined) {
      setValidationError(validationResponse);
    } else {
      setValidationError(undefined);

      dispatch(
        updateEditableProps({
          entityId:
            !constructionElementEntityId || constructionElementEntityId === ""
              ? constructionId
              : constructionElementEntityId.toString(),
          entityName: constructionElementsName,
          propertyName: constructionElementProperty,
          propertyValue: newValue !== undefined && newValue !== null ? String(newValue) : "",
        }),
      );
    }
  };

  const handleCheckboxChange = (checked: boolean): void => {
    setNewValue(checked);
    handleBlur();
  };

  const handleTextAndSelectChange = (updatedValue: string | undefined): void => {
    setNewValue(updatedValue ?? "");
  };

  const handleNumberChange = (updatedValue: number): void => {
    setNewValue(updatedValue);
  };

  const handleBlur = (): void => {
    stopEdit();
    void validateProperty();
  };

  const startEdit = (): void => {
    setNewValue(value);
    setValidationError(undefined);
    setIsEditing(true);
  };
  const stopEdit = (): void => {
    setIsEditing(false);
  };

  const editableComponent = (editableType: EditableTypeDto | undefined): JSX.Element | null => {
    switch (editableType) {
      case EditableTypeDto.CheckBox:
        return (
          <EditableItem
            isEditing={isEditing}
            onStartEdit={startEdit}
            editContent={<IsaacCheckBox newValue={newValue as boolean} handleValueChanged={handleCheckboxChange} />}
            viewContent={newValue === true ? <input type="checkbox" checked /> : <input type="checkbox" />}
          />
        );

      case EditableTypeDto.Text:
        return (
          <EditableItem
            isEditing={isEditing}
            onStartEdit={startEdit}
            editContent={
              <IsaacTextInput
                newValue={newValue as string}
                handleValueChanged={handleTextAndSelectChange}
                handleBlur={handleBlur}
              />
            }
            viewContent={<ConstructionTableFixedCell value={formatCell(newValue)} />}
          />
        );

      case EditableTypeDto.Number:
        return (
          <EditableItem
            isEditing={isEditing}
            onStartEdit={startEdit}
            editContent={
              <IsaacNumberInput
                newValue={newValue as number}
                handleValueChanged={handleNumberChange}
                handleBlur={handleBlur}
              />
            }
            viewContent={<ConstructionTableFixedCell value={formatCell(newValue)} />}
          />
        );

      case EditableTypeDto.Select:
        console.log(newValue);
        return (
          <EditableItem
            isEditing={isEditing}
            onStartEdit={startEdit}
            editContent={
              <IsaacDropDown
                constructionElementsName={constructionElementsName}
                constructionElementProperty={constructionElementProperty}
                newValue={newValue}
                handleValueChanged={handleTextAndSelectChange}
                handleBlur={handleBlur}
                extraInfo={extraInfo}
              />
            }
            viewContent={<ConstructionTableFixedCell value={formatCell(newValue)} />}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className={`${styles.container} ${styles.editingContainer} ${validationError ? styles.error : ""}`}>
      {editableComponent(type)}

      <IsaacLoading loading={loading} size={SpinnerSize.small} message="" />

      {validationError && !loading && (
        <div title={validationError} style={{display: "inline-block", cursor: "pointer"}}>
          <Icon iconName="Error" className={styles.errorIcon} />
        </div>
      )}
    </div>
  );
};

export default ConstructionTableEditableCell;
